import React from "react";
import Button from "components/Button/Regular";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Table from "../../components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { ClientRow, getClientsList } from "services/lists.service";
import { deleteOrgContact } from "services/contacts.service";
import vgToast from "utils/toast";

const ClientsList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();
  const [clients, setClients] = React.useState<ClientRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Client-side paging and sorting: the list endpoints return whole
  // collections with no paging parameters, so there is nothing to page
  // server-side against.
  const view = useTableView(clients);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0) {
      setClients([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getClientsList(token, orgId);
      if (apiError) {
        setError(apiError.message);
        setClients([]);
        return;
      }
      setClients(data);
    } catch (e) {
      setError((e as Error).message);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this client? This cannot be undone.")) return;
    if (orgId <= 0) return;
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteOrgContact(token, orgId, Number(id));
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Clients"
      subTitle="Manage the families you are helping."
      actions={
        <Button onClick={() => navigate("/app/clients/new-customer")}>
          <PlusIcon />
          <span>Add Customer</span>
        </Button>
      }
    >
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="mb-3"
      />

      <Table<{
        id: string;
        image: string;
        name: string;
        email: string;
      }>
        loading={loading}
        columns={{
          id: "Client Id",
          image: "Client Image",
          name: "Client Name",
          email: "Email Address",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No clients yet. Add your first customer to get started."
        handleViewItem={(id) => navigate(`/app/clients/${id}`)}
        handleRemoveItem={handleDelete}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar
          from={view.from}
          to={view.to}
          total={view.total}
          currentPage={view.page}
          totalPages={view.totalPages}
          onPrev={view.goPrev}
          onNext={view.goNext}
          onPageSelect={view.goToPage}
        />
      </div>
    </Page>
  );
};

export default ClientsList;
